import { useCallback, useEffect, useState } from 'react';
import {
  apiClient,
  getImpersonationToken,
  removeImpersonationToken,
  setImpersonationToken,
} from '../../services/apiClient';

export interface TargetOrganization {
  id: string;
  name?: string | null;
  slug?: string | null;
  display_name?: string | null;
}

export interface ImpersonationSession {
  id: string;
  actor_user_uid: string;
  actor_organization_id: string;
  target_organization_id: string;
  partner_id?: string | null;
  started_at: string;
  expires_at: string;
  ended_at?: string | null;
  end_reason?: string | null;
  reason?: string | null;
  target_organization?: TargetOrganization;
}

interface StartArgs {
  targetOrganizationId: string;
  reason?: string;
}

/**
 * 代行アクセス (インパーソネート) フック。
 *
 * - sessionStorage に代行 JWT を保持 (タブ独立)
 * - 起動時 / `sj:impersonation-expired` イベント受信時にサーバーの
 *   /api/impersonation/sessions/current で整合性を確認
 * - start: POST /api/impersonation/sessions → 代行 JWT 発行
 * - end: DELETE /api/impersonation/sessions/current → sessionStorage クリア
 */
export function useImpersonation() {
  const [currentSession, setCurrentSession] = useState<ImpersonationSession | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(() => Boolean(getImpersonationToken()));

  const refresh = useCallback(async () => {
    if (!getImpersonationToken()) {
      setCurrentSession(null);
      setIsLoading(false);
      return;
    }
    try {
      const res = await apiClient.get('/impersonation/sessions/current');
      if (res.status === 204) {
        // サーバー側で既に終了済みだが sessionStorage にトークンが残っている
        removeImpersonationToken();
        setCurrentSession(null);
      } else {
        setCurrentSession(res.data as ImpersonationSession);
      }
    } catch {
      setCurrentSession(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    if (typeof window === 'undefined') return;
    const handler = () => {
      void refresh();
    };
    window.addEventListener('sj:impersonation-expired', handler);
    window.addEventListener('sj:impersonation-changed', handler);
    return () => {
      window.removeEventListener('sj:impersonation-expired', handler);
      window.removeEventListener('sj:impersonation-changed', handler);
    };
  }, [refresh]);

  const start = useCallback(async ({ targetOrganizationId, reason }: StartArgs) => {
    const res = await apiClient.post('/impersonation/sessions', {
      target_organization_id: targetOrganizationId,
      reason,
    });
    const token = res.data?.token as string | undefined;
    const session = res.data?.session as ImpersonationSession | undefined;
    if (!token || !session) {
      throw new Error('Invalid response: missing token/session');
    }
    setImpersonationToken(token);
    setCurrentSession(session);
    if (typeof window !== 'undefined') {
      // 他の useImpersonation インスタンス (AppLayout 等) にも反映させる
      window.dispatchEvent(new CustomEvent('sj:impersonation-changed'));
    }
    return session;
  }, []);

  const end = useCallback(async () => {
    try {
      await apiClient.delete('/impersonation/sessions/current');
    } catch {
      // 失敗しても sessionStorage は破棄する (孤立トークン防止)
    }
    removeImpersonationToken();
    setCurrentSession(null);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sj:impersonation-changed'));
    }
  }, []);

  return {
    currentSession,
    isImpersonating: Boolean(currentSession),
    isLoading,
    start,
    end,
    refresh,
  };
}
